import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  applyActionCode,
  confirmPasswordReset as fbConfirmPasswordReset,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  multiFactor,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  verifyPasswordResetCode,
  type User,
} from 'firebase/auth';
import { useQueryClient } from '@tanstack/react-query';
import type { OrgRole, OrgSummary, PlatformRole } from '@leanopx/shared';
import { auth } from '@/lib/firebase';
import { readActiveOrgId, writeActiveOrgId } from '@/lib/active-org';
import { listMemberships } from '@/lib/orgs-client';
import { logger, warnUnlessAbort } from '@/lib/logger';

/**
 * Active organization — the tenant all org-scoped reads/writes target.
 * The tuple of { id, name, role } is denormalized from the user's
 * memberships so pages can render without a second fetch.
 */
export interface ActiveOrg {
  id: string;
  name: string;
  role: OrgRole;
}

interface AuthContextValue {
  user: User | null;
  platformRole: PlatformRole | null;
  activeOrg: ActiveOrg | null;
  memberships: OrgSummary[];
  /** True when the signed-in user has at least one enrolled MFA factor. */
  hasMfa: boolean;
  loading: boolean;
  switchOrg: (orgId: string) => Promise<void>;
  refreshClaims: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (code: string, newPassword: string) => Promise<void>;
  applyEmailVerification: (code: string) => Promise<void>;
  resendEmailVerification: () => Promise<void>;
  signOutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Read `platformRole` + `orgs` from the user's ID-token result. The old
 * single-tier `role` claim is gone — every org scopes its own `OrgRole`.
 */
function readClaims(tokenResult: { claims: Record<string, unknown> }): {
  platformRole: PlatformRole | null;
  orgsClaim: Record<string, OrgRole>;
} {
  const rawPlatform = tokenResult.claims['platformRole'];
  const platformRole: PlatformRole | null = rawPlatform === 'superAdmin' ? 'superAdmin' : null;

  const rawOrgs = tokenResult.claims['orgs'];
  const orgsClaim: Record<string, OrgRole> = {};
  if (rawOrgs && typeof rawOrgs === 'object') {
    for (const [id, role] of Object.entries(rawOrgs as Record<string, unknown>)) {
      if (role === 'admin' || role === 'member') {
        orgsClaim[id] = role;
      }
    }
  }
  return { platformRole, orgsClaim };
}

/**
 * Pick the active org id from (in order): localStorage preference if
 * still a valid membership, otherwise the first membership, otherwise
 * null.
 */
function pickActiveOrgId(memberships: OrgSummary[], preferredId: string | null): string | null {
  if (preferredId && memberships.some((m) => m.id === preferredId)) {
    return preferredId;
  }
  return memberships[0]?.id ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [platformRole, setPlatformRole] = useState<PlatformRole | null>(null);
  const [memberships, setMemberships] = useState<OrgSummary[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [hasMfa, setHasMfa] = useState(false);
  const [loading, setLoading] = useState(true);

  // Hydrate from Firebase auth state + custom claims + /api/orgs.
  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      // Reset to loading for every auth change so the chrome won't
      // render between `setUser(u)` and `setActiveOrgId(chosen)` —
      // otherwise sidebar-nav / portal-rail mount with `activeOrg ===
      // null` and `useActiveOrg()` throws into the global
      // ErrorBoundary (observed on prod first-sign-in: AppShellInner
      // had already passed its `loading` gate from the initial
      // signed-out callback, so the subsequent signed-in callback
      // raced setUser → setActiveOrgId across the await boundary).
      setLoading(true);
      setUser(u);
      if (!u) {
        setPlatformRole(null);
        setMemberships([]);
        setActiveOrgId(null);
        writeActiveOrgId(null);
        setHasMfa(false);
        setLoading(false);
        return;
      }

      try {
        const tokenResult = await u.getIdTokenResult();
        const { platformRole: pRole, orgsClaim } = readClaims(tokenResult);
        setPlatformRole(pRole);
        setHasMfa(multiFactor(u).enrolledFactors.length > 0);

        // Claims give us the org-ids + roles cheaply. Fetch /api/orgs to
        // decorate with names and the (optional) primaryOrgId. When the
        // server isn't reachable yet (e.g. pre-deploy dogfood), fall
        // back to a synthetic list built from the claim so the UI can at
        // least render the switcher with ids.
        let list: OrgSummary[] = [];
        let primaryOrgId: string | null = null;
        try {
          const resp = await listMemberships();
          list = resp.organizations;
          primaryOrgId = resp.primaryOrgId ?? null;
        } catch (err) {
          warnUnlessAbort('auth: listMemberships failed, falling back to claim', err);
          list = Object.entries(orgsClaim).map(([id, role]) => ({
            id,
            name: id,
            slug: id,
            role,
          }));
        }
        setMemberships(list);

        const stored = readActiveOrgId();
        // `pickActiveOrgId` already falls back to `memberships[0]` when
        // the preferred id isn't a valid membership — so a single call
        // (stored → primary → first) is enough. No need to chain.
        const chosen = pickActiveOrgId(list, stored ?? primaryOrgId);
        setActiveOrgId(chosen);
        writeActiveOrgId(chosen);
      } finally {
        setLoading(false);
      }
    });
  }, []);

  const activeOrg: ActiveOrg | null = useMemo(() => {
    if (!activeOrgId) return null;
    const m = memberships.find((x) => x.id === activeOrgId);
    if (!m) return null;
    return { id: m.id, name: m.name, role: m.role };
  }, [activeOrgId, memberships]);

  const switchOrg = useCallback(
    async (orgId: string) => {
      const target = memberships.find((m) => m.id === orgId);
      if (!target) {
        throw new Error(`org ${orgId} is not a membership`);
      }
      writeActiveOrgId(orgId);
      setActiveOrgId(orgId);
      // Blow away cached per-tenant data so nothing from the old org
      // leaks into the new one before its own fetch lands.
      queryClient.invalidateQueries();
    },
    [memberships, queryClient],
  );

  const refreshClaims = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) return;
    const tokenResult = await current.getIdTokenResult(true);
    const { platformRole: pRole, orgsClaim } = readClaims(tokenResult);
    setPlatformRole(pRole);
    // Also refresh the membership list — a freshly-accepted invite
    // shows up here after the server stamps the claim.
    try {
      const resp = await listMemberships();
      setMemberships(resp.organizations);
      const stored = readActiveOrgId();
      const chosen = pickActiveOrgId(resp.organizations, stored ?? resp.primaryOrgId ?? null);
      setActiveOrgId(chosen);
      writeActiveOrgId(chosen);
    } catch (err) {
      warnUnlessAbort('auth: refreshClaims listMemberships failed', err);
      // Fall back to the claim map if the server is unreachable.
      setMemberships(
        Object.entries(orgsClaim).map(([id, role]) => ({
          id,
          name: id,
          slug: id,
          role,
        })),
      );
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(cred.user);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      const code = (err as { code?: string }).code ?? 'unknown';
      const message = (err as Error).message ?? '';
      logger.error(`signInWithGoogle failed [${code}] ${message}`, err);
      throw err;
    }
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  const confirmPasswordReset = useCallback(async (code: string, newPassword: string) => {
    await verifyPasswordResetCode(auth, code);
    await fbConfirmPasswordReset(auth, code, newPassword);
  }, []);

  const applyEmailVerification = useCallback(async (code: string) => {
    await applyActionCode(auth, code);
  }, []);

  const resendEmailVerification = useCallback(async () => {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
    }
  }, []);

  const signOutUser = useCallback(async () => {
    // Resilient sign-out: even if `signOut(auth)` throws or hangs (which
    // it does when the Firebase Auth SDK lands in a wedged state from a
    // corrupted refresh token — `securetoken.googleapis.com` returns 400
    // on every refresh, which the SDK sometimes tries to do during
    // sign-out), we MUST still get the user back to a clean slate.
    //
    // Three layers, each independent:
    //   1. Try the SDK's normal sign-out. Works on healthy sessions.
    //   2. Manually purge the Firebase Auth IndexedDB store
    //      (`firebaseLocalStorageDb`) — that's where the refresh token
    //      actually lives. If it's corrupted, this is the only way to
    //      clear it.
    //   3. Wipe the active-org localStorage marker so the next sign-in
    //      doesn't try to land on a stale tenant.
    //
    // Finally, force a hard navigation to `/sign-in` (not a SPA navigate
    // — we want React state + the AuthProvider to fully reset).
    try {
      await signOut(auth);
    } catch (err) {
      logger.warn('auth.signOut.failed', { err: err instanceof Error ? err.message : String(err) });
    }
    try {
      await purgeFirebaseAuthIndexedDb();
    } catch (err) {
      logger.warn('auth.signOut.indexedDb-purge-failed', {
        err: err instanceof Error ? err.message : String(err),
      });
    }
    writeActiveOrgId(null);
    if (typeof window !== 'undefined') {
      window.location.assign('/sign-in');
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        platformRole,
        activeOrg,
        memberships,
        hasMfa,
        loading,
        switchOrg,
        refreshClaims,
        signIn,
        signUp,
        signInWithGoogle,
        sendPasswordReset,
        confirmPasswordReset,
        applyEmailVerification,
        resendEmailVerification,
        signOutUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Returns the active org, or throws if there is none. Use on pages that
 * sit behind the authed gate — by the time they mount, `requireAuth` has
 * already bounced users with zero memberships to `/onboarding/no-org`
 * and the switchOrg picker has selected one.
 */
export function useActiveOrg(): ActiveOrg {
  const { activeOrg } = useAuth();
  if (!activeOrg) {
    throw new Error('useActiveOrg called with no active org — is this page behind requireAuth?');
  }
  return activeOrg;
}

/** True for platform superadmins. */
export function useIsSuperAdmin(): boolean {
  const { platformRole } = useAuth();
  return platformRole === 'superAdmin';
}

/**
 * True for users who can manage the current org — either the active-org
 * admin or any platform superadmin. SuperAdmin escape hatches the org
 * admin gate (they can always manage any tenant by design).
 */
export function useIsOrgAdmin(): boolean {
  const { activeOrg, platformRole } = useAuth();
  if (platformRole === 'superAdmin') return true;
  return activeOrg?.role === 'admin';
}

/**
 * Force-purge the Firebase Auth IndexedDB store + localStorage entries.
 *
 * The Firebase Auth Web SDK persists the signed-in user — including the
 * refresh token — in an IndexedDB database named `firebaseLocalStorageDb`
 * (single object store `firebaseLocalStorage`). When the refresh token
 * lands in a malformed state (a popup-postMessage failure under a
 * restrictive `Cross-Origin-Opener-Policy`, for example), every
 * `getIdToken()` 400s and the SDK gets stuck. The normal `signOut(auth)`
 * call can fail to clear the corrupt entry because the SDK tries to
 * round-trip through the same token-refresh path during sign-out.
 *
 * This helper bypasses the SDK and deletes the IndexedDB store directly,
 * then purges the legacy `firebase:authUser:*` localStorage shim entries
 * (older SDK versions used localStorage; current versions only fall back
 * to it). Safe to call from healthy sessions too — it's idempotent: the
 * SDK rebuilds the store on next sign-in.
 *
 * Only runs in the browser; harmless no-op on SSR / Node test envs.
 */
async function purgeFirebaseAuthIndexedDb(): Promise<void> {
  if (typeof window === 'undefined') return;
  // Delete the IndexedDB store if present. When IndexedDB is blocked or
  // unavailable (incognito / iframe / older browser) the SDK falls back
  // to localStorage — so the localStorage sweep below must run
  // independently of this guard, otherwise the very environments most
  // likely to land in a wedged state get no recovery.
  if (window.indexedDB) {
    await new Promise<void>((resolve) => {
      const req = window.indexedDB.deleteDatabase('firebaseLocalStorageDb');
      req.onsuccess = () => resolve();
      req.onerror = () => resolve(); // SDK will rebuild on next sign-in; non-fatal
      req.onblocked = () => resolve(); // another tab has it open; best-effort
    });
  }
  // Purge the legacy localStorage shim entries. Modern SDKs only use
  // localStorage as a fallback when IndexedDB is unavailable, but old
  // sessions may have planted entries here that the SDK still reads.
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('firebase:authUser:')) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage can be blocked in some private-mode / iframe contexts.
    // Non-fatal; we'll redirect to /sign-in regardless.
  }
}
